'use client';

async function invokeEmail(type: string, to: string, data: Record<string, any>): Promise<void> {
  try {
    const res = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, to, data }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      console.warn(`[emailService] Failed to send ${type} email:`, (body as any)?.error || res.statusText);
    }
  } catch (e: any) {
    console.warn(`[emailService] Error sending ${type} email:`, e?.message);
  }
}

export const emailService = {
  async sendRegistrationConfirmation(params: {
    to: string;
    fullName: string;
    studentNumber: string;
    courseName: string;
    intake: string;
    courseFee: number;
    accessCode: string;
  }) {
    await invokeEmail('registration_confirmation', params.to, {
      fullName: params.fullName,
      studentNumber: params.studentNumber,
      courseName: params.courseName,
      intake: params.intake,
      courseFee: params.courseFee.toLocaleString(),
      accessCode: params.accessCode,
    });
  },

  async sendPaymentReceipt(params: {
    to: string;
    fullName: string;
    studentNumber: string;
    courseName: string;
    courseFee: number;
    accessCode: string;
  }) {
    await invokeEmail('payment_receipt', params.to, {
      fullName: params.fullName,
      studentNumber: params.studentNumber,
      courseName: params.courseName,
      courseFee: params.courseFee.toLocaleString(),
      accessCode: params.accessCode,
    });
  },

  async sendExamResult(params: {
    to: string;
    fullName: string;
    examTitle: string;
    score: number;
    totalMarks: number;
    percentage: number;
    passMark: number;
    passed: boolean;
  }) {
    await invokeEmail('exam_result', params.to, {
      fullName: params.fullName,
      examTitle: params.examTitle,
      score: params.score,
      totalMarks: params.totalMarks,
      percentage: params.percentage,
      passMark: params.passMark,
      passed: params.passed,
    });
  },

  async sendExamResultToAdmin(params: {
    adminEmail: string;
    studentName: string;
    studentNumber: string;
    examTitle: string;
    courseName: string;
    score: number;
    totalMarks: number;
    percentage: number;
    passed: boolean;
  }) {
    await invokeEmail('exam_result_admin', params.adminEmail, {
      studentName: params.studentName,
      studentNumber: params.studentNumber,
      examTitle: params.examTitle,
      courseName: params.courseName,
      score: params.score,
      totalMarks: params.totalMarks,
      percentage: params.percentage,
      passed: params.passed,
    });
  },

  async sendCertificateLecturerAlert(params: {
    lecturerEmail: string;
    lecturerName: string;
    studentName: string;
    studentNumber: string;
    courseName: string;
    score: number;
    totalMarks: number;
    percentage: number;
    issueDate: string;
  }) {
    await invokeEmail('certificate_lecturer_alert', params.lecturerEmail, {
      lecturerName: params.lecturerName,
      studentName: params.studentName,
      studentNumber: params.studentNumber,
      courseName: params.courseName,
      score: params.score,
      totalMarks: params.totalMarks,
      percentage: params.percentage,
      issueDate: params.issueDate,
    });
  },

  async sendCertificateIssued(params: {
    to: string;
    fullName: string;
    studentNumber: string;
    courseName: string;
    issueDate: string;
  }) {
    await invokeEmail('certificate_issued', params.to, {
      fullName: params.fullName,
      studentNumber: params.studentNumber,
      courseName: params.courseName,
      issueDate: params.issueDate,
    });
  },

  async sendWithdrawalUpdate(params: {
    to: string;
    fullName: string;
    amount: number;
    status: 'approved' | 'rejected' | 'paid';
    provider: string;
    number: string;
  }) {
    await invokeEmail('withdrawal_approved', params.to, {
      fullName: params.fullName,
      amount: params.amount.toFixed(2),
      status: params.status,
      provider: params.provider,
      number: params.number,
    });
  },

  async sendLecturerApplicationStatus(params: {
    to: string;
    fullName: string;
    status: 'approved' | 'rejected';
    reason?: string;
  }) {
    await invokeEmail('lecturer_application_status', params.to, {
      fullName: params.fullName,
      status: params.status,
      reason: params.reason || '',
    });
  },

  async sendDirectEmail(params: {
    to: string;
    subject: string;
    body: string;
    attachments?: { filename: string; content: string }[];
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'direct_email',
          to: params.to,
          data: {
            subject: params.subject,
            body: params.body,
            attachments: params.attachments || [],
          },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return { success: false, error: (body as any)?.error || `HTTP ${res.status}` };
      }
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e?.message || 'Unknown error' };
    }
  },
};
